import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Star } from 'lucide-react'
import { api } from '@/lib/api'
import { useT } from '@/lib/i18n'
import type { ApiError } from '@/lib/api'
import type { ProductReview } from '@/shared/types'

interface Props {
  productId: number
  ratingAvg: number
  ratingCount: number
  canReview: boolean
  reviews: ProductReview[]
  isMayParfum?: boolean
}

function Stars({ value, size = 'h-4 w-4' }: { value: number; size?: string }) {
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((n) => (
        <Star
          key={n}
          className={`${size} ${n <= Math.round(value) ? 'fill-current text-[#d7b171]' : 'text-gray-300'}`}
        />
      ))}
    </div>
  )
}

export function ReviewsSection({
  productId,
  ratingAvg,
  ratingCount,
  canReview,
  reviews = [],
  isMayParfum = false,
}: Props) {
  const T = useT('product')
  const queryClient = useQueryClient()
  const [formOpen, setFormOpen] = useState(false)
  const [rating, setRating] = useState(5)
  const [comment, setComment] = useState('')
  const [submitted, setSubmitted] = useState(false)

  const submitMutation = useMutation<unknown, ApiError>({
    mutationFn: () =>
      api.post(`/products/${productId}/reviews`, { rating, comment: comment.trim() }),
    onSuccess: async () => {
      setSubmitted(true)
      setFormOpen(false)
      await queryClient.invalidateQueries({ queryKey: ['product'] })
    },
  })

  return (
    <div className={`mt-6 border-t pt-4 ${isMayParfum ? 'border-[#657086]' : 'border-gray-100'}`}>
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Stars value={ratingAvg} />
          <span className={`text-sm font-semibold ${isMayParfum ? 'text-[#211915]' : 'text-gray-900'}`}>
            {ratingCount > 0 ? ratingAvg.toFixed(1) : '—'}
          </span>
          <span className="text-xs text-gray-500">
            {ratingCount > 0 ? T.reviewsCount(ratingCount) : T.noReviewsYet}
          </span>
        </div>
        {canReview && !formOpen && !submitted && (
          <button
            type="button"
            onClick={() => setFormOpen(true)}
            className={`text-xs font-semibold underline ${isMayParfum ? 'text-[#9d733a]' : 'text-brand-600'}`}
          >
            {T.writeReview}
          </button>
        )}
      </div>

      {submitted && <p className="mt-3 text-xs text-gray-500">{T.reviewSubmitted}</p>}

      {formOpen && (
        <div className="mt-3 space-y-2 rounded-2xl border border-gray-200 p-3">
          <div className="flex items-center gap-1">
            {[1, 2, 3, 4, 5].map((n) => (
              <button key={n} type="button" onClick={() => setRating(n)}>
                <Star
                  className={`h-6 w-6 ${n <= rating ? 'fill-current text-[#d7b171]' : 'text-gray-300'}`}
                />
              </button>
            ))}
          </div>
          <textarea
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder={T.reviewCommentPlaceholder}
            rows={3}
            className="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
          />
          {submitMutation.isError && (
            <p className="text-xs text-red-500">{T.reviewSubmitFailed}</p>
          )}
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => submitMutation.mutate()}
              disabled={submitMutation.isPending}
              className={`rounded-xl px-4 py-2 text-xs font-semibold text-white disabled:opacity-50 ${
                isMayParfum ? 'bg-[#bc9050]' : 'bg-brand-600'
              }`}
            >
              {submitMutation.isPending ? T.reviewSubmitting : T.reviewSubmit}
            </button>
            <button
              type="button"
              onClick={() => setFormOpen(false)}
              className="rounded-xl border border-gray-200 px-4 py-2 text-xs font-semibold text-gray-600"
            >
              {T.reviewCancel}
            </button>
          </div>
        </div>
      )}

      {reviews.length > 0 && (
        <ul className="mt-4 space-y-3">
          {reviews.map((review) => (
            <li
              key={review.id}
              className={`rounded-2xl p-3 border ${
                isMayParfum
                  ? 'bg-[#1f242d] border-[#444e5d]/60 text-slate-200'
                  : 'bg-slate-900/60 border-slate-800 text-slate-200'
              }`}
            >
              <div className="flex items-center justify-between gap-2">
                <span className="text-xs font-semibold text-slate-100">{review.author_name}</span>
                <Stars value={review.rating} size="h-3 w-3" />
              </div>
              {review.comment && <p className="mt-1.5 text-sm text-slate-300">{review.comment}</p>}
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
